import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Code, Plus, ExternalLink, Paperclip, X, Award, Star, Trash2, CheckCircle, Clock } from 'lucide-react';

const PROJECT_TYPES = [
  { value: 'scratch', label: 'Scratch' },
  { value: 'python', label: 'Python' },
  { value: 'web', label: 'Web / HTML' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'codeorg', label: 'Code.org' },
  { value: 'other', label: 'Other' },
];

const STATUS_BADGE = {
  submitted: { class: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Submitted' },
  reviewed:  { class: 'bg-blue-100 text-blue-700',   icon: CheckCircle, label: 'Reviewed' },
  approved:  { class: 'bg-emerald-100 text-emerald-700', icon: Star, label: 'Approved' },
  needs_revision: { class: 'bg-red-100 text-red-700', icon: X, label: 'Needs revision' },
};

export const StudentCodingProjects = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', project_type: 'scratch', url: '', preview_image: '' });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/student/coding-submissions');
      setItems(res.data || []);
    } catch { toast.error('Failed to load projects'); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSubmit = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    if (!form.url.trim() && files.length === 0) return toast.error('Provide a URL or upload a file');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('project_type', form.project_type);
      fd.append('url', form.url);
      fd.append('preview_image', form.preview_image);
      files.forEach(f => fd.append('files', f));
      await api.post('/student/coding-submissions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Project submitted!');
      setDialogOpen(false);
      setForm({ title: '', description: '', project_type: 'scratch', url: '', preview_image: '' });
      setFiles([]);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await api.delete(`/student/coding-submissions/${id}`); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Code className="w-6 h-6 text-violet-600" /> My Projects
          </h2>
          <p className="text-slate-500 font-semibold text-sm">Share your coding projects and get teacher feedback</p>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-700 font-bold" onClick={() => setDialogOpen(true)} data-testid="new-project-btn">
          <Plus className="w-4 h-4 mr-1" /> New Project
        </Button>
      </div>

      {loading ? <div className="text-center py-12 text-slate-500">Loading…</div> :
       items.length === 0 ? (
         <div className="nb-card text-center py-12">
           <Code className="w-12 h-12 text-slate-300 mx-auto mb-3" />
           <p className="text-slate-500 font-semibold">No projects yet</p>
           <p className="text-xs text-slate-400 mt-1">Share your first project — Scratch, Python, web — anything!</p>
         </div>
       ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {items.map(p => {
             const badge = STATUS_BADGE[p.status] || STATUS_BADGE.submitted;
             const Icon = badge.icon;
             return (
               <div key={p.id} className="nb-card p-4" data-testid={`project-card-${p.id}`}>
                 {p.preview_image && (
                   <img src={p.preview_image} alt={p.title} className="w-full h-32 object-cover rounded-lg mb-3 border-2 border-slate-900" />
                 )}
                 <div className="flex items-start justify-between mb-2">
                   <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">{p.project_type}</span>
                   <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.class}`}><Icon className="w-3 h-3 inline mr-1" />{badge.label}</span>
                 </div>
                 <h3 className="text-lg font-bold text-slate-900 mb-1">{p.title}</h3>
                 <p className="text-xs text-slate-500 line-clamp-2 mb-2">{p.description}</p>
                 {p.url && (
                   <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline mb-2">
                     <ExternalLink className="w-3 h-3" /> Open project
                   </a>
                 )}
                 {(p.files || []).length > 0 && (
                   <div className="flex flex-wrap gap-1 mb-2">
                     {p.files.map((f, i) => (
                       <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                         className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded hover:bg-blue-200">📎 {f.original_name || f.filename}</a>
                     ))}
                   </div>
                 )}
                 {p.teacher_feedback && (
                   <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800 mb-2">
                     <strong>Teacher:</strong> {p.teacher_feedback}
                   </div>
                 )}
                 {(p.badges_earned || []).length > 0 && (
                   <div className="flex flex-wrap gap-1 mb-2">
                     {p.badges_earned.map((b, i) => (
                       <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full inline-flex items-center gap-1">
                         <Award className="w-3 h-3" /> {b}
                       </span>
                     ))}
                   </div>
                 )}
                 {p.is_showcase && (
                   <div className="text-[10px] font-bold text-amber-700 mb-2"><Star className="w-3 h-3 inline" /> Featured in Showcase</div>
                 )}
                 {p.status === 'submitted' && (
                   <Button size="sm" variant="outline" className="w-full border-red-400 text-red-700" onClick={() => handleDelete(p.id)} data-testid={`delete-project-${p.id}`}>
                     <Trash2 className="w-3 h-3 mr-1" /> Delete
                   </Button>
                 )}
               </div>
             );
           })}
         </div>
       )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold">Share a Project</DialogTitle>
            <DialogDescription>Show off what you built. Link to Scratch/GitHub/CodePen or upload files.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="font-bold">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Maze Maker"
                className="mt-1" data-testid="project-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-bold">Type</Label>
                <Select value={form.project_type} onValueChange={v => setForm(p => ({ ...p, project_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="project-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-bold">Preview image URL (optional)</Label>
                <Input value={form.preview_image} onChange={e => setForm(p => ({ ...p, preview_image: e.target.value }))} placeholder="https://…"
                  className="mt-1" data-testid="project-preview" />
              </div>
            </div>
            <div>
              <Label className="font-bold">Project URL</Label>
              <Input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://scratch.mit.edu/projects/…"
                className="mt-1" data-testid="project-url" />
            </div>
            <div>
              <Label className="font-bold">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What does it do? What did you learn?" className="mt-1 min-h-[100px]" data-testid="project-desc" />
            </div>
            <div>
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full" data-testid="project-attach">
                <Paperclip className="w-4 h-4 mr-1" /> Attach files (zip / image / .py — max 25 MB each)
              </Button>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={e => setFiles([...files, ...Array.from(e.target.files || [])])} />
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-100 rounded px-2 py-1">
                      <span className="truncate flex-1">{f.name}</span>
                      <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-red-500 ml-2"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={onSubmit} disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 font-bold" data-testid="submit-project-btn">
              {saving ? 'Submitting…' : 'Submit Project'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentCodingProjects;
